import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BookOpen,
  UserPlus,
  Upload,
  CheckCircle,
  User,
  Info,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FilePlus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { notificationsAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import './Notifications.css';

const NOTIFICATION_ICONS = {
  welcome: Bell,
  content_uploaded: Upload,
  module_created: BookOpen,
  module_assigned: BookOpen,
  module_enrolled: UserPlus,
  trainee_enrolled: UserPlus,
  content_completed: CheckCircle,
  content_added_to_module: FilePlus,
  module_deleted: Trash2,
  content_removed_from_module: XCircle,
};

const NOTIFICATION_ICON_COLORS = {
  welcome: 'var(--color-medium-green)',
  content_uploaded: '#2563eb',
  module_created: '#7c3aed',
  module_assigned: '#7c3aed',
  module_enrolled: '#059669',
  trainee_enrolled: '#059669',
  content_completed: '#16a34a',
  content_added_to_module: '#2563eb',
  module_deleted: '#dc2626',
  content_removed_from_module: '#dc2626',
};

const formatNotificationTime = (createdAt) => {
  if (!createdAt) {
    return 'Just now';
  }

  const notificationDate = new Date(createdAt);
  if (Number.isNaN(notificationDate.getTime())) {
    return 'Just now';
  }

  const diffMs = Date.now() - notificationDate.getTime();
  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays}d ago`;
  }

  return notificationDate.toLocaleDateString();
};

const Notifications = () => {
  const navigate = useNavigate();
  const { showNotification } = useAppContext();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const limit = 20;

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit,
        unreadOnly: filter === 'unread',
      };
      const response = await notificationsAPI.getNotifications(params);
      const data = response.data?.data;

      if (data) {
        setNotifications(data.items || []);
        setUnreadCount(data.unreadCount || 0);
        setTotalCount(data.totalCount || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.error || 'Failed to load notifications';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId, actionUrl) => {
    const target = notifications.find((n) => n.id === notificationId);
    if (target && !target.isRead) {
      try {
        const response = await notificationsAPI.markAsRead(notificationId);
        const nextUnreadCount = response.data?.data?.unreadCount;
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
        if (typeof nextUnreadCount === 'number') {
          setUnreadCount(nextUnreadCount);
        } else {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err) {
        const message = err?.response?.data?.error;
        if (message && err?.response?.status < 500) {
          showNotification(message, 'error');
        }
      }
    }

    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true }))
      );
      setUnreadCount(0);
      showNotification('All notifications marked as read', 'success');
    } catch (err) {
      const message = err?.response?.data?.error;
      if (message && err?.response?.status < 500) {
        showNotification(message, 'error');
      }
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleRetry = () => {
    fetchNotifications();
  };

  const getNotificationIcon = (type) => {
    const Icon = NOTIFICATION_ICONS[type] || Info;
    return Icon;
  };

  const getNotificationIconColor = (type) => {
    return NOTIFICATION_ICON_COLORS[type] || 'var(--text-secondary)';
  };

  return (
    <div className="notifications-page">
      {/* Header */}
      <div className="notifications-page__header">
        <div className="notifications-page__header-left">
          <h1 className="notifications-page__title">Notifications</h1>
          <span className="notifications-page__subtitle">
            {totalCount > 0
              ? `${totalCount} notification${totalCount !== 1 ? 's' : ''}`
              : 'Stay updated with your latest activities'}
          </span>
        </div>
        <div className="notifications-page__header-right">
          {unreadCount > 0 && (
            <button
              type="button"
              className="notifications-page__mark-all-btn"
              onClick={handleMarkAllAsRead}
            >
              <CheckCircle size={16} />
              <span>Mark all as read</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="notifications-page__tabs">
        <button
          type="button"
          className={`notifications-page__tab ${filter === 'all' ? 'notifications-page__tab--active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
          {totalCount > 0 && (
            <span className="notifications-page__tab-count">{totalCount}</span>
          )}
        </button>
        <button
          type="button"
          className={`notifications-page__tab ${filter === 'unread' ? 'notifications-page__tab--active' : ''}`}
          onClick={() => handleFilterChange('unread')}
        >
          Unread
          {unreadCount > 0 && (
            <span className="notifications-page__tab-count notifications-page__tab-count--unread">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="notifications-page__content">
        {/* Loading State */}
        {loading && (
          <div className="notifications-page__state">
            <div className="notifications-page__spinner" />
            <p className="notifications-page__state-title">Loading notifications</p>
            <span className="notifications-page__state-subtitle">
              Checking for your latest updates.
            </span>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="notifications-page__state">
            <AlertCircle size={40} className="notifications-page__state-icon" />
            <p className="notifications-page__state-title">Something went wrong</p>
            <span className="notifications-page__state-subtitle">{error}</span>
            <button
              type="button"
              className="notifications-page__retry-btn"
              onClick={handleRetry}
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && notifications.length === 0 && (
          <div className="notifications-page__state">
            <Bell size={40} className="notifications-page__state-icon" />
            <p className="notifications-page__state-title">No notifications</p>
            <span className="notifications-page__state-subtitle">
              {filter === 'unread'
                ? 'You have no unread notifications.'
                : 'You have no notifications right now.'}
            </span>
          </div>
        )}

        {/* Notification List */}
        {!loading && !error && notifications.length > 0 && (
          <div className="notifications-page__list">
            {notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.type);
              const iconColor = getNotificationIconColor(notification.type);

              return (
                <div
                  key={notification.id}
                  className={`notifications-page__item ${!notification.isRead ? 'notifications-page__item--unread' : ''}`}
                  onClick={() =>
                    handleMarkAsRead(notification.id, notification.actionUrl)
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMarkAsRead(notification.id, notification.actionUrl);
                    }
                  }}
                >
                  <div
                    className="notifications-page__item-icon"
                    style={{ color: iconColor }}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="notifications-page__item-content">
                    <div className="notifications-page__item-header">
                      <span className="notifications-page__item-title">
                        {notification.title}
                      </span>
                      <span className="notifications-page__item-time">
                        {formatNotificationTime(notification.createdAt)}
                      </span>
                    </div>
                    <p className="notifications-page__item-message">
                      {notification.message}
                    </p>
                    <span className="notifications-page__item-action">
                      {notification.actionUrl
                        ? 'View details'
                        : 'Click to view'}
                    </span>
                  </div>
                  {!notification.isRead && (
                    <div className="notifications-page__item-dot" />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="notifications-page__pagination">
            <button
              type="button"
              className="notifications-page__page-btn"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </button>
            <span className="notifications-page__page-info">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="notifications-page__page-btn"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              <span>Next</span>
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
